// Try-it demo (a) — mini budget dashboard. A collapsible 2-section
// hierarchy with quarterly Budget/Actual/Committed/Variance cells; clicking
// a quarter cell opens a drill-down drawer with the underlying SAP rows and
// committed invoices, mirroring the real CellDrilldownDrawer (the +4,000
// accrual row carries its "accrual" badge next to the matched invoice —
// no accrual-chain links: the linker is deferred this phase).
// All data embedded; zero network calls.

import React, { useEffect, useState } from 'react';
import { fmtCell, fmtUSD } from './mockData';

const SECTIONS = [
  {
    owner: 'Kyle',
    categories: [
      {
        name: 'Controversy',
        lines: [
          {
            id: 'irs',
            name: 'IRS Assistance',
            io: 'TAX1450',
            matters: '29122 / 29864',
            annualB: 72000,
            q: { 1: { b: 18000, a: 15500, c: 0 }, 2: { b: 18000, a: 0, c: 6000 } },
          },
          {
            id: 'tp',
            name: 'Transfer Pricing Report - Canada',
            io: 'TAX1456',
            oneTime: true,
            annualB: 10000,
            q: { 1: { b: 10000, a: 12000, c: 0 }, 2: { b: 0, a: 0, c: 0 } },
          },
        ],
      },
    ],
  },
  {
    owner: 'Clayton',
    categories: [
      {
        name: 'Dues & Subscriptions',
        lines: [
          {
            id: 'gartner',
            name: 'Gartner Subscription',
            io: 'TAX1402',
            annualB: 35000,
            q: { 1: { b: 8750, a: 8750, c: 0 }, 2: { b: 8750, a: 8750, c: 0 } },
          },
          {
            id: 'tei',
            name: 'TEI Membership',
            io: 'TAX1402',
            annualB: 20000,
            q: { 1: { b: 20000, a: 19500, c: 0 }, 2: { b: 0, a: 0, c: 0 } },
          },
        ],
      },
    ],
  },
];

// Drill-down rows per (line, quarter). The IRS Q1/Q2 cells replay the
// accrual timeline so the auditability story connects back to it.
const DRILL = {
  'irs:1': {
    sap: [
      { doc: '1900000123', type: 'KR', date: 'Mar 12, 2026', det: 'Fenwick · ref INV-20114', src: 'invoice match', conf: '97%', amt: 6000 },
      { doc: '1900000287', type: 'KR', date: 'Apr 2, 2026', det: 'Deloitte · ref 9912', src: 'invoice match', conf: '97%', amt: 5500 },
      { doc: 'VSSC000981', type: 'AB', date: 'Apr 30, 2026', det: 'Q1 ACCRUAL — IRS ASSISTANCE', src: 'invoice match', conf: '100%', amt: 4000, accrual: true, matched: 'invoice #9955' },
    ],
    inv: [],
    note: 'Invoice #9955 left Committed the moment the accrual matched it — its $4,000 lives in Actual, above.',
  },
  'irs:2': {
    sap: [
      { doc: 'VSSC000981R', type: 'AB', date: 'May 1, 2026', det: 'ACCRUAL REVERSAL', src: 'carried from accrual', conf: '100%', amt: -4000, accrual: true },
      { doc: '1900000456', type: 'KR', date: 'May 9, 2026', det: 'Deloitte · ref 9955', src: 'invoice match', conf: '100%', amt: 4000 },
    ],
    inv: [
      { num: '#10240', vendor: 'Fenwick', date: 'Jun 3, 2026', status: 'submitted to AP', amt: 6000 },
    ],
    note: 'Reversal and posting cancel — the net Q2 effect of invoice #9955 is zero.',
  },
  'tp:1': {
    sap: [
      { doc: '4700001102', type: 'LG', date: 'Mar 28, 2026', det: '29301 TRANSFER PRICING CANADA', src: 'matter code', conf: '100%', amt: 12000 },
    ],
    inv: [],
    note: 'Over budget by $2,000 — which is why the variance cell is highlighted.',
  },
  'gartner:1': {
    sap: [
      { doc: '1900000789', type: 'KR', date: 'Feb 15, 2026', det: 'Gartner Inc · ANNUAL RESEARCH SEAT', src: 'learned mapping', conf: '95%', amt: 8750 },
    ],
    inv: [],
  },
  'gartner:2': {
    sap: [
      { doc: '1900001022', type: 'KR', date: 'May 15, 2026', det: 'Gartner Inc · ANNUAL RESEARCH SEAT', src: 'learned mapping', conf: '95%', amt: 8750 },
    ],
    inv: [],
  },
  'tei:1': {
    sap: [
      { doc: '1900000201', type: 'KR', date: 'Feb 20, 2026', det: 'TEI · ANNUAL MEMBERSHIP DUES', src: 'learned mapping', conf: '92%', amt: 19500 },
    ],
    inv: [],
  },
};

const GROUPS = [
  { id: 'q1', label: 'Q1', q: 1 },
  { id: 'q2', label: 'Q2', q: 2 },
  { id: 'annual', label: 'Annual', q: null },
];
const MEASURES = [
  { k: 'b', label: 'Bud' },
  { k: 'a', label: 'Act' },
  { k: 'c', label: 'Com' },
  { k: 'v', label: 'Var' },
];

function cellOf(line, q) {
  if (q != null) return line.q[q];
  return {
    b: line.annualB,
    a: line.q[1].a + line.q[2].a,
    c: line.q[1].c + line.q[2].c,
  };
}

function withVar(cell) {
  return { ...cell, v: cell.a + cell.c - cell.b };
}

function sumCells(lines, q) {
  return withVar(
    lines.reduce(
      (acc, l) => {
        const c = cellOf(l, q);
        return { b: acc.b + c.b, a: acc.a + c.a, c: acc.c + c.c };
      },
      { b: 0, a: 0, c: 0 }
    )
  );
}

function MeasureCells({ cell, clickable, onOpen }) {
  return MEASURES.map(({ k }, i) => {
    const over = k === 'v' && cell.v > 0;
    const classes = ['numcell', 'num'];
    if (i === 0) classes.push('group-edge');
    if (over) classes.push('var-over');
    if (clickable) classes.push('clickable');
    return (
      <td
        key={k}
        className={classes.join(' ')}
        onClick={clickable ? onOpen : undefined}
        title={clickable ? 'Click to see the rows behind this cell' : undefined}
      >
        {fmtCell(cell[k])}
      </td>
    );
  });
}

function Drawer({ drill, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const data = DRILL[`${drill.lineId}:${drill.q}`] || { sap: [], inv: [] };

  return (
    <>
      <div className="tut-drawer-backdrop" onClick={onClose} />
      <aside className="tut-drawer" role="dialog" aria-modal="true">
        <div className="tut-drawer-h">
          <div>
            <div className="t">{drill.lineName}</div>
            <div className="sub code">FY2026 · Q{drill.q}</div>
          </div>
          <button type="button" className="tut-drawer-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="tut-drawer-body">
          <div className="tut-dr-sec">
            <div className="tut-dr-label">SAP rows · Actual ({data.sap.length})</div>
            {data.sap.length === 0 ? (
              <div className="tut-dr-empty">No SAP postings in this cell.</div>
            ) : (
              <table className="tut-dr-table">
                <thead>
                  <tr>
                    <th>Doc</th>
                    <th>Posted</th>
                    <th>Details</th>
                    <th>Source</th>
                    <th className="r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sap.map((r) => (
                    <tr key={r.doc}>
                      <td>
                        <div className="doc code">{r.doc}</div>
                        <div className="meta">
                          {r.type}
                          {r.accrual && <span className="tut-badge accrual">accrual</span>}
                          {r.matched && <span className="tut-badge match">→ {r.matched}</span>}
                        </div>
                      </td>
                      <td className="dt">{r.date}</td>
                      <td className="det">{r.det}</td>
                      <td>
                        <span className="src">{r.src}</span>
                        <span className="tut-conf num">{r.conf}</span>
                      </td>
                      <td className={`r num${r.amt < 0 ? ' neg' : ''}`}>{fmtUSD(r.amt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="tut-dr-sec">
            <div className="tut-dr-label">Committed invoices ({data.inv.length})</div>
            {data.inv.length === 0 ? (
              <div className="tut-dr-empty">No committed invoices in this cell.</div>
            ) : (
              <table className="tut-dr-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Vendor</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.inv.map((inv) => (
                    <tr key={inv.num}>
                      <td className="doc code">{inv.num}</td>
                      <td>{inv.vendor}</td>
                      <td className="dt">{inv.date}</td>
                      <td>
                        <span className="tut-badge status">{inv.status}</span>
                      </td>
                      <td className="r num">{fmtUSD(inv.amt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {data.note && <div className="tut-dr-note">{data.note}</div>}
        </div>
      </aside>
    </>
  );
}

export default function DemoDashboard() {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [drill, setDrill] = useState(null);

  const toggle = (owner) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(owner)) next.delete(owner);
      else next.add(owner);
      return next;
    });
  };

  const allLines = SECTIONS.flatMap((s) => s.categories.flatMap((c) => c.lines));

  return (
    <div className="tut-dash-wrap">
      <table className="tut-dash-table">
        <thead>
          <tr>
            <th className="nm" rowSpan={2}>
              Budget line
            </th>
            {GROUPS.map((g) => (
              <th key={g.id} colSpan={4} className="group-edge ctr">
                {g.label}
              </th>
            ))}
          </tr>
          <tr>
            {GROUPS.map((g) =>
              MEASURES.map((m, i) => (
                <th key={`${g.id}-${m.k}`} className={i === 0 ? 'group-edge' : ''}>
                  {m.label}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {SECTIONS.map((sec) => {
            const ownerLines = sec.categories.flatMap((c) => c.lines);
            const isCollapsed = collapsed.has(sec.owner);
            return (
              <React.Fragment key={sec.owner}>
                <tr className="row-owner">
                  <td className="nm">
                    <button type="button" className="tut-twist" onClick={() => toggle(sec.owner)}>
                      {isCollapsed ? '▸' : '▾'}
                    </button>
                    {sec.owner}
                  </td>
                  {GROUPS.map((g) => (
                    <MeasureCells key={g.id} cell={sumCells(ownerLines, g.q)} />
                  ))}
                </tr>
                {!isCollapsed &&
                  sec.categories.map((cat) => (
                    <React.Fragment key={cat.name}>
                      <tr className="row-cat">
                        <td className="nm ind1">{cat.name}</td>
                        {GROUPS.map((g) => (
                          <MeasureCells key={g.id} cell={sumCells(cat.lines, g.q)} />
                        ))}
                      </tr>
                      {cat.lines.map((line) => (
                        <tr key={line.id} className="row-line">
                          <td className="nm ind2" title={`IO ${line.io}${line.matters ? ` · Matters ${line.matters}` : ''}`}>
                            {line.name}
                            {line.oneTime && <span className="tut-badge soft">one-time</span>}
                          </td>
                          {GROUPS.map((g) => (
                            <MeasureCells
                              key={g.id}
                              cell={withVar(cellOf(line, g.q))}
                              clickable={g.q != null}
                              onOpen={() =>
                                setDrill({ lineId: line.id, lineName: line.name, q: g.q })
                              }
                            />
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            );
          })}
          <tr className="row-grand">
            <td className="nm">Grand total · FY2026</td>
            {GROUPS.map((g) => (
              <MeasureCells key={g.id} cell={sumCells(allLines, g.q)} />
            ))}
          </tr>
        </tbody>
      </table>
      <div className="tut-dash-note">
        All amounts USD. Q3–Q4 are hidden here for space — the real dashboard shows all four
        quarters, a monthly view, and prior-year columns.
      </div>
      {drill && <Drawer drill={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}
